import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { youtubeRssService } from '@/lib/youtubeRss';
import { useAuth } from '@/hooks/useAuth';

export interface Creator {
  id: string;
  name: string;
  avatar: string;
  subscribers: number;
  isSubscribed: boolean;
}

export const useCreators = () => {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [subscribedCreators, setSubscribedCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiQuotaExceeded, setApiQuotaExceeded] = useState(false);
  const [searchQuotaExceeded, setSearchQuotaExceeded] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Save creator info to Supabase
  const saveCreatorToSupabase = async (creator: Creator) => {
    try {
      const { error } = await supabase
        .from('creators')
        .upsert({
          creator_id: creator.id,
          name: creator.name,
          avatar: creator.avatar,
          subscribers: creator.subscribers,
          is_subscribed: creator.isSubscribed
        }, { 
          onConflict: 'creator_id',
          ignoreDuplicates: false 
        });
      
      if (error) {
        console.error('Error saving creator to Supabase:', error);
      }
    } catch (error) {
      console.error('Error in saveCreatorToSupabase:', error);
    }
  };

  // Get creators from Supabase
  const getCreatorsFromSupabase = async (subscriptionIds?: string[]): Promise<Creator[]> => {
    try {
      let query = supabase.from('creators').select('*');
      
      if (subscriptionIds && subscriptionIds.length > 0) {
        query = query.in('creator_id', subscriptionIds);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching creators from Supabase:', error);
        return [];
      }
      
      return data?.map(creator => ({
        id: creator.creator_id,
        name: creator.name,
        avatar: creator.avatar,
        subscribers: creator.subscribers,
        isSubscribed: creator.is_subscribed
      })) || [];
    } catch (error) {
      console.error('Error in getCreatorsFromSupabase:', error);
      return [];
    }
  };

  // Get real creator info from YouTube API for missing creators
  const getCreatorInfoFromAPI = async (channelId: string): Promise<Creator | null> => {
    try {
      const API_KEY = await api.getApiKey();
      if (!API_KEY) {
        return null;
      }

      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&id=${channelId}&key=${API_KEY}`
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (!data.items || data.items.length === 0) {
        return null;
      }

      const item = data.items[0];
      return {
        id: channelId,
        name: item.snippet.title,
        avatar: item.snippet.thumbnails.medium?.url || item.snippet.thumbnails.default?.url,
        subscribers: parseInt(item.statistics.subscriberCount || '0', 10),
        isSubscribed: true
      };
    } catch (error) {
      console.log(`Couldn't fetch API data for channel ${channelId}:`, error);
    }
    return null;
  };

  // Create fallback creators from subscription IDs using cached video data
  const getCreatorsFromVideoCache = async (subscriptionIds: string[]): Promise<Creator[]> => {
    try {
      console.log('Getting creators from video cache for IDs:', subscriptionIds);
      
      // Try to get creator info from both cached_videos_feed and videos tables
      const [cacheResults, videosResults] = await Promise.all([
        supabase
          .from('cached_videos_feed')
          .select('channel_id, channel_name, channel_avatar')
          .in('channel_id', subscriptionIds),
        supabase
          .from('videos')
          .select('channel_id, channel_name, channel_avatar')
          .in('channel_id', subscriptionIds)
      ]);
      
      // Create a map to avoid duplicates
      const creatorMap = new Map();
      
      // Process cache results
      cacheResults.data?.forEach(creator => {
        if (!creatorMap.has(creator.channel_id) && creator.channel_name && creator.channel_name.trim()) {
          creatorMap.set(creator.channel_id, {
            id: creator.channel_id,
            name: creator.channel_name,
            avatar: creator.channel_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(creator.channel_name)}&background=random&color=fff`,
            subscribers: 0,
            isSubscribed: true
          });
        }
      });
      
      // Process videos results
      videosResults.data?.forEach(creator => {
        if (!creatorMap.has(creator.channel_id) && creator.channel_name && creator.channel_name.trim()) {
          creatorMap.set(creator.channel_id, {
            id: creator.channel_id,
            name: creator.channel_name,
            avatar: creator.channel_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(creator.channel_name)}&background=random&color=fff`,
            subscribers: 0,
            isSubscribed: true
          });
        }
      });
      
      // For missing creators, try to get real info from API
      const missingCreatorIds = subscriptionIds.filter(id => !creatorMap.has(id));
      console.log(`Trying to fetch ${missingCreatorIds.length} missing creators from API`);
      
      for (const channelId of missingCreatorIds) {
        const apiCreator = await getCreatorInfoFromAPI(channelId);
        if (apiCreator) {
          creatorMap.set(channelId, apiCreator);
          // Save to Supabase for future use
          await saveCreatorToSupabase(apiCreator);
        } else {
          // Final fallback with better naming
          creatorMap.set(channelId, {
            id: channelId,
            name: `Channel ${channelId.slice(-8)}`, // Use part of the channel ID for uniqueness
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(`CH`)}&background=random&color=fff`,
            subscribers: 0,
            isSubscribed: true
          });
        }
      }
      
      const creators = Array.from(creatorMap.values());
      console.log(`Created ${creators.length} creators from video cache and API`);
      
      return creators;
    } catch (error) {
      console.error('Error fetching creators from video cache:', error);
      // Enhanced fallback with better naming
      return subscriptionIds.map((id) => ({
        id,
        name: `Channel ${id.slice(-8)}`, // Use part of the channel ID for uniqueness
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(`CH`)}&background=random&color=fff`,
        subscribers: 0,
        isSubscribed: true
      }));
    }
  };
  
  const fetchCreators = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let subscriptions: string[] = [];
      
      if (user) {
        const { data: userProfile, error: profileError } = await supabase
          .from('profiles')
          .select('subscriptions')
          .eq('id', user.id)
          .single();
        
        if (!profileError && userProfile && userProfile.subscriptions) {
          subscriptions = userProfile.subscriptions;
          console.log(`Found ${subscriptions.length} subscriptions for user`);
        }
      }
      
      // Always get subscribed creators first
      if (subscriptions.length > 0) {
        // 1. Try to get from creators table first
        let supabaseCreators = await getCreatorsFromSupabase(subscriptions);
        console.log(`Found ${supabaseCreators.length} creators in Supabase creators table`);
        
        // 2. If we don't have all creators, get missing ones from video cache
        const missingCreatorIds = subscriptions.filter(id => 
          !supabaseCreators.some(creator => creator.id === id)
        );
        
        if (missingCreatorIds.length > 0) {
          console.log(`Getting ${missingCreatorIds.length} missing creators from video cache`);
          const missingCreators = await getCreatorsFromVideoCache(missingCreatorIds);
          
          // Save missing creators to Supabase for future use
          for (const creator of missingCreators) {
            await saveCreatorToSupabase(creator);
          }
          
          // Combine all creators
          supabaseCreators = [...supabaseCreators, ...missingCreators];
        }
        
        setSubscribedCreators(supabaseCreators);
        console.log(`Set ${supabaseCreators.length} subscribed creators`);
        
        toast({
          title: "Créateurs chargés",
          description: `${supabaseCreators.length} créateurs trouvés dans vos abonnements.`,
          variant: "default"
        });
      } else {
        setSubscribedCreators([]);
        console.log('No subscriptions found');
      }
      
      
      // Use Supabase cache for popular creators
      try {
        console.log('🔄 Getting popular creators from Supabase cache...');
        
        const { data: cachedCreators, error } = await supabase
          .from('creators')
          .select('*')
          .order('subscribers', { ascending: false })
          .limit(20);

        if (error) throw error;

        if (cachedCreators && cachedCreators.length > 0) {
          const mappedCreators = cachedCreators.map(creator => ({
            id: creator.creator_id,
            name: creator.name,
            avatar: creator.avatar,
            subscribers: creator.subscribers,
            isSubscribed: subscriptions.includes(creator.creator_id)
          }));
          
          setCreators(mappedCreators);
          console.log(`✅ Set ${mappedCreators.length} creators from Supabase cache`);
        } else {
          setError('Aucun créateur trouvé dans le cache');
        }
      } catch (cacheError) {
        console.error('❌ Cache fallback failed:', cacheError);
        setError('Impossible de charger les créateurs');
      }
    } catch (err) {
      console.error('Error fetching creators:', err);
      setError('Failed to fetch creators');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleSubscription = async (creatorId: string) => {
    try {
      if (!user) {
        toast({
          title: 'Connexion requise',
          description: 'Veuillez vous connecter pour gérer vos abonnements',
          variant: 'destructive',
        });
        return;
      }
      
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('subscriptions')
        .eq('id', user.id)
        .single();
      
      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching user profile:', profileError);
        toast({
          title: 'Error',
          description: 'Failed to update subscription. Please try again.',
          variant: 'destructive',
        });
        return;
      }
      
      let subscriptions = userProfile?.subscriptions || [];
      
      if (!Array.isArray(subscriptions)) {
        subscriptions = [];
      }
      
      const isCurrentlySubscribed = subscriptions.includes(creatorId);
      
      if (isCurrentlySubscribed) {
        subscriptions = subscriptions.filter(id => id !== creatorId);
      } else {
        subscriptions.push(creatorId);
      }
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ subscriptions })
        .eq('id', user.id);
      
      if (updateError) {
        console.error('Error updating user profile:', updateError);
        toast({
          title: 'Error',
          description: 'Failed to update subscription. Please try again.',
          variant: 'destructive',
        });
        return;
      }
      
      setCreators(prevCreators => 
        prevCreators.map(creator => {
          if (creator.id === creatorId) {
            const updatedCreator = {
              ...creator,
              isSubscribed: !isCurrentlySubscribed,
              subscribers: !isCurrentlySubscribed 
                ? creator.subscribers + 1 
                : creator.subscribers - 1
            };
            // Save updated creator to Supabase
            saveCreatorToSupabase(updatedCreator);
            return updatedCreator;
          }
          return creator;
        })
      );
      
      if (!isCurrentlySubscribed) {
        const creatorInList = subscribedCreators.find(c => c.id === creatorId);
        const creatorFromMainList = creators.find(c => c.id === creatorId);
        
        if (!creatorInList && creatorFromMainList) {
          const newSubscribedCreator = { ...creatorFromMainList, isSubscribed: true };
          setSubscribedCreators(prev => [...prev, newSubscribedCreator]);
          await saveCreatorToSupabase(newSubscribedCreator);
        } else if (!creatorInList) {
          // Use YouTube API for creator info
          let newCreator: Creator | null = null;
          
          console.log(`🔄 Getting creator info via YouTube API...`);
          newCreator = await getCreatorInfoFromAPI(creatorId);
          
          if (newCreator) {
            newCreator.isSubscribed = true;
            console.log(`✅ Found creator: ${newCreator.name}`);
          } else {
            console.log(`⚠️ Using generic creator fallback for ${creatorId}`);
            newCreator = {
              id: creatorId,
              name: `Channel ${creatorId.slice(-8)}`,
              avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(`CH`)}&background=random&color=fff`,
              subscribers: 0,
              isSubscribed: true
            };
          }
          
          setSubscribedCreators(prev => [...prev, newCreator!]);
          await saveCreatorToSupabase(newCreator!);
        }
      } else {
        setSubscribedCreators(prev => prev.filter(c => c.id !== creatorId));
      }
      
      const creator = creators.find(c => c.id === creatorId);
      if (creator) {
        toast({
          title: !isCurrentlySubscribed ? "Abonnement effectué" : "Désabonnement effectué",
          description: !isCurrentlySubscribed
            ? `Vous êtes maintenant abonné à ${creator.name}`
            : `Vous n'êtes plus abonné à ${creator.name}`,
        });
      }
    } catch (err) {
      console.error('Error in toggleSubscription:', err);
      toast({
        title: 'Error',
        description: 'Failed to update subscription. Please try again.',
        variant: 'destructive',
      });
    }
  };
  
  const searchCreators = async (query: string) => {
    if (!query.trim()) return [];
    
    try {
      setSearchQuotaExceeded(false);
      console.log(`🔍 Starting creator search for: "${query}"`);
      
      let searchResults: Creator[] = [];
      
      try {
        console.log('🔄 Searching creators via YouTube API...');
        searchResults = await api.searchYouTubeCreators(query);
      } catch (youtubeError: any) {
        console.error('❌ YouTube API search failed:', youtubeError);
        
        // Try fallback to Supabase cache search
        console.log('🔍 Trying fallback search in Supabase cache...');
        const { data: supabaseCreators, error: supabaseError } = await supabase
          .from('creators')
          .select('*')
          .ilike('name', `%${query}%`)
          .limit(20);
        
        if (supabaseError) {
          console.error('❌ Supabase search also failed:', supabaseError);
          throw new Error('YouTube API and Supabase search failed');
        }
        
        if (supabaseCreators && supabaseCreators.length > 0) {
          console.log(`✅ Found ${supabaseCreators.length} creators in Supabase cache`);
          searchResults = supabaseCreators.map(creator => ({
            id: creator.creator_id,
            name: creator.name,
            avatar: creator.avatar,
            subscribers: creator.subscribers,
            isSubscribed: creator.is_subscribed
          }));
        } else {
          console.log('⚠️ No results in Supabase cache either');
          searchResults = [];
        }
      }
      
      if (searchResults.length > 0) {
        console.log(`✅ Found ${searchResults.length} creators total`);
        
        // Save search results to Supabase for future use
        for (const creator of searchResults) {
          await saveCreatorToSupabase(creator);
        }
        
        return searchResults;
      }
      
      console.log('❌ No creators found');
      return [];
    } catch (error: any) {
      console.error('❌ Error searching creators:', error);
      
      if (error?.message?.includes('quota') || 
          (error?.response?.data?.error?.errors && 
          error?.response?.data?.error?.errors.some((e: any) => e.reason === 'quotaExceeded'))) {
        
        setSearchQuotaExceeded(true);
        setApiQuotaExceeded(true);
        
        toast({
          title: "Limite API YouTube atteinte",
          description: "Recherche dans le cache local activée.",
          variant: "default"
        });
        
        // Try to search in Supabase creators as final fallback
        const { data: supabaseCreators } = await supabase
          .from('creators')
          .select('*')
          .ilike('name', `%${query}%`);
        
        const filteredCreators = supabaseCreators?.map(creator => ({
          id: creator.creator_id,
          name: creator.name,
          avatar: creator.avatar,
          subscribers: creator.subscribers,
          isSubscribed: creator.is_subscribed
        })) || [];
        
        return filteredCreators.length > 0 ? filteredCreators : [];
      }
      
      // More descriptive error message
      toast({
        title: "Erreur de recherche",
        description: `Impossible de rechercher des créateurs: ${error.message || 'Service temporairement indisponible'}`,
        variant: "destructive"
      });
      
      return [];
    }
  };

  // Function to update creators with generic names
  const updateGenericCreators = async () => {
    if (!user || apiQuotaExceeded) return;

    console.log('Checking for creators with generic names to update...');
    
    // Find creators with generic names (Channel + ID or Créateur + number)
    const creatorsToUpdate = subscribedCreators.filter(creator => 
      creator.name.startsWith('Créateur ') || 
      creator.name.startsWith('Channel ') ||
      creator.subscribers === 0
    );

    if (creatorsToUpdate.length === 0) {
      console.log('No creators need updating');
      return;
    }

    console.log(`Updating ${creatorsToUpdate.length} creators with real data...`);
    
    for (const creator of creatorsToUpdate) {
      try {
        const updatedCreator = await getCreatorInfoFromAPI(creator.id);
        if (updatedCreator) {
          // Update in local state
          setSubscribedCreators(prev => 
            prev.map(c => c.id === creator.id ? updatedCreator : c)
          );
          
          // Save to Supabase
          await saveCreatorToSupabase(updatedCreator);
          
          console.log(`Updated creator ${creator.id} with real name: ${updatedCreator.name}`);
          
          // Add a small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`Failed to update creator ${creator.id}:`, error);
      }
    }
    
    if (creatorsToUpdate.length > 0) {
      toast({
        title: "Créateurs mis à jour",
        description: `${creatorsToUpdate.length} créateur(s) ont été mis à jour avec leurs vraies informations.`,
        variant: "default"
      });
    }
  };
  
  useEffect(() => {
    fetchCreators();
  }, [user]);

  // Auto-update generic creators after initial load
  useEffect(() => {
    if (!loading && subscribedCreators.length > 0 && !apiQuotaExceeded) {
      const timer = setTimeout(() => {
        updateGenericCreators();
      }, 2000); // Wait 2 seconds after loading to update
      
      return () => clearTimeout(timer);
    }
  }, [loading, subscribedCreators.length, apiQuotaExceeded]);
  
  return {
    creators,
    subscribedCreators,
    loading,
    error,
    apiQuotaExceeded,
    searchQuotaExceeded,
    toggleSubscription,
    searchCreators,
    refetch: fetchCreators,
    updateGenericCreators
  };
};
